import React, { useState } from 'react';
import { ChevronDown, Plus, Trash2, Lock } from 'lucide-react';
import './InspectionApp.css';
import api from './services/api';
import arenaMap from './Arena Ice Surface Map - DONE.jpg';
import { useAuth } from './AuthContext';

// Import inspection data from JSON
import inspectionData from './inspectionData.json';

// Employees will be loaded from backend

const isNeedsAttentionInspectionAnswer = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'needs-attention' || normalized === 'needs attention' || normalized === 'no';
};

const buildInspectionDeficienciesPayload = (answers = {}, deficiencies = {}, deficiencyCorrected = {}) => {
  return Object.entries(answers).reduce((accumulator, [questionIndex, answerValue]) => {
    if (!isNeedsAttentionInspectionAnswer(answerValue)) {
      return accumulator;
    }

    accumulator[questionIndex] = {
      text: String(deficiencies[questionIndex] || '').trim(),
      corrected: Boolean(deficiencyCorrected[questionIndex])
    };

    return accumulator;
  }, {});
};

const formatEmployeeDisplayName = (employee = {}) => {
  const firstName = String(employee?.firstName || '').trim();
  const lastName = String(employee?.lastName || '').trim();

  if (lastName && firstName) {
    return `${lastName}, ${firstName}`;
  }

  return lastName || firstName || String(employee?.email || '').trim();
};

const compareEmployeesByLastFirst = (left = {}, right = {}) => {
  const lastNameComparison = String(left?.lastName || '').trim().localeCompare(
    String(right?.lastName || '').trim(),
    undefined,
    { sensitivity: 'base' }
  );

  if (lastNameComparison !== 0) {
    return lastNameComparison;
  }

  return String(left?.firstName || '').trim().localeCompare(
    String(right?.firstName || '').trim(),
    undefined,
    { sensitivity: 'base' }
  );
};

export default function InspectionApp() {
  const { user } = useAuth();
  const currentUserName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || user?.username || user?.email || '';
  const [currentDate, setCurrentDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [currentTime, setCurrentTime] = useState(() => new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }));

  const refreshDateTime = React.useCallback(() => {
    const now = new Date();
    setCurrentDate(now.toISOString().split('T')[0]);
    setCurrentTime(now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }));
  }, []);
  
  const [formState, setFormState] = useState('login');
  const [inspectionCategory, setInspectionCategory] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedKitType, setSelectedKitType] = useState(''); // for First Aid Kit Inspection
  const [leadInspector, setLeadInspector] = useState(currentUserName || '');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [inspectionAnswers, setInspectionAnswers] = useState({});
  const [deficiencies, setDeficiencies] = useState({});
  const [deficiencyCorrected, setDeficiencyCorrected] = useState({});
  const [workerObservations, setWorkerObservations] = useState('');
  const [inspectionNotes, setInspectionNotes] = useState([]);
  const [noteInput, setNoteInput] = useState('');
  const [isFormCompleted, setIsFormCompleted] = useState(false);
  const [leadSignature, setLeadSignature] = useState(false);
  const [participantSignatures, setParticipantSignatures] = useState({});
  const [showParticipantDropdown, setShowParticipantDropdown] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employeesError, setEmployeesError] = useState('');

  // Parks/Playground locations (address + display name)
  const parksLocations = [
    { address: '408013 1172 Drive W, Diamond Valley, AB TOL 0H0', name: 'Field of Dreams Park' },
    { address: '444 5 St SW, Diamond Valley, AB T0L 0H0', name: 'Legacy Fitness Park' },
    { address: '4 Avenue SE, Diamond Valley, AB T0L 0H0', name: 'Maplewood Park' },
    { address: '132 Riverwood Way SW, Diamond Valley, AB TOL 0H0', name: 'Riverwood Park' },
    { address: '444 5 St SW, Diamond Valley, AB T0L 0H0', name: 'Skateboard Park I' },
    { address: '444 5 St SW, Diamond Valley, AB T0L 0H0', name: 'Skate Park II - C. Kendrick Memorial' },
    { address: '415 4A Street NW, Diamond Valley, AB TOL 0H0', name: 'Wilrich Park' },
    { address: '522 Country Meadows Street NW, Diamond Valley, AB TOL 0H0', name: 'Country Meadows Playground' },
    { address: '416 Edgar Ave NW, Diamond Valley, AB TOL 0H0', name: 'Edgar Playground' },
    { address: '318 Robert Street SW, Diamond Valley, AB T0L 0H0', name: 'Perdue Playground' },
    { address: '1516 Southwood Road SE, Diamond Valley, AB TOL 0H0', name: 'Bailey Ridge Playground' }
  ];

  // Utilities Inspection locations (address + display name)
  const utilitiesLocations = [
    { name: 'Lagoon Blower', address: '400 – 3 Street NE' },
    { name: 'Lagoon Transfer', address: '600 – 3 Street NE' },
    { name: 'Administration Lift Stn.', address: '301 Centre Ave West' },
    { name: '2 Street North Lift Stn.', address: '210 – 2 Street North' },
    { name: '490 Sunset Lift Stn.', address: '490 Sunset Blvd SE' },
    { name: 'East Distribution Pumphouse', address: '600 – 1 Street SE' },
    { name: 'East Distribution Metering', address: 'East of 615 – 1 Street SE' },
    { name: 'West Old Chlorine Bldg.', address: '865 Caulkins Pl SE' },
    { name: 'Caulkins Lift Stn.', address: '625 Caulkins Pl SE' },
    { name: 'Seclusion Valley Lift Stn.', address: '410 Seclusion Valley Dr NE' },
    { name: 'Royalite Life Lift Stn.', address: '202 Royalite Way SE' }
  ];

  // Helper to encode/decode parks selection as address::name
  const encodeParkValue = (address, name) => `${address}::${name}`;
  const parseParkValue = (val) => {
    const [address, name] = (val || '').split('::');
    return { address, name };
  };

  // Helper to encode/decode utilities selection as address::name
  const encodeUtilityValue = (address, name) => `${address}::${name}`;
  const parseUtilityValue = (val) => {
    const [address, name] = (val || '').split('::');
    return { address, name };
  };

  const handleStartInspection = () => {
    refreshDateTime();
    setFormState('inspection');
  };

  // Load employees from backend
  React.useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoadingEmployees(true);
        setEmployeesError('');
        const res = await api.get('/api/users', { params: { scope: 'non-fire' } });
        const list = (res.data?.users || []).map(u => ({
          id: u.email || `${u.firstName || ''}.${u.lastName || ''}`.toLowerCase(),
          firstName: String(u.firstName || '').trim(),
          lastName: String(u.lastName || '').trim(),
          email: String(u.email || '').trim(),
          name: formatEmployeeDisplayName(u),
          role: u.jobTitle || u.role || 'Employee',
          department: u.department || ''
        }));
        list.sort(compareEmployeesByLastFirst);
        setEmployees(list);
      } catch (err) {
        console.error('Failed to load employees:', err);
        setEmployeesError('Unable to load employees');
      } finally {
        setLoadingEmployees(false);
      }
    };
    fetchEmployees();
  }, []);

  React.useEffect(() => {
    if (currentUserName) {
      setLeadInspector(currentUserName);
    }
  }, [currentUserName]);

  // Lead Inspector is always the safety office contact

  const handleCategoryChange = (e) => {
    setInspectionCategory(e.target.value);
    setSelectedLocation('');
    setSelectedKitType('');
    setInspectionAnswers({});
    setDeficiencies({});
    setDeficiencyCorrected({});
  };

  const handleLocationChange = (e) => {
    setSelectedLocation(e.target.value);
    setInspectionAnswers({});
    setDeficiencies({});
    setDeficiencyCorrected({});
  };
  
  const handleKitTypeChange = (e) => {
    setSelectedKitType(e.target.value);
    setInspectionAnswers({});
    setDeficiencies({});
    setDeficiencyCorrected({});
  };

  const toggleParticipant = (employeeId) => {
    setSelectedParticipants(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleAnswer = (questionIndex, answer) => {
    setInspectionAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
    if (!isNeedsAttentionInspectionAnswer(answer)) {
      setDeficiencies(prev => {
        const newDef = { ...prev };
        delete newDef[questionIndex];
        return newDef;
      });

      setDeficiencyCorrected(prev => {
        const nextCorrected = { ...prev };
        delete nextCorrected[questionIndex];
        return nextCorrected;
      });
    }
  };

  const handleDeficiencyNote = (questionIndex, note) => {
    setDeficiencies(prev => ({
      ...prev,
      [questionIndex]: note
    }));
  };

  const handleAddNote = () => {
    if (noteInput.trim()) {
      setInspectionNotes(prev => [...prev, {
        id: Date.now(),
        text: noteInput,
        timestamp: new Date().toLocaleString(),
        editable: !isFormCompleted
      }]);
      setNoteInput('');
    }
  };

  const handleDeleteNote = (noteId) => {
    setInspectionNotes(prev => prev.filter(note => note.id !== noteId));
  };

  const getCurrentQuestions = () => {
    if (!inspectionCategory) return [];
    // For WHMIS: use WHMIS questionnaire regardless of location selection
    if (inspectionCategory === 'whmis') {
      const whmisQs = inspectionData['whmis']?.questionnaires || {};
      const firstKey = Object.keys(whmisQs)[0];
      return (firstKey && whmisQs[firstKey]) ? whmisQs[firstKey] : [];
    }
    // For First Aid: questionnaire is driven by kit type; locations mirror facility for context
    if (inspectionCategory === 'first-aid') {
      if (!selectedLocation || !selectedKitType) return [];
      return inspectionData['first-aid']?.questionnaires[selectedKitType] || [];
    }
    // Parks: choose questionnaire type based on selected site name (Playground vs Park)
    if (inspectionCategory === 'parks') {
      if (!selectedLocation) return [];
      const { name } = parseParkValue(selectedLocation);
      const label = String(name || '').trim();
      const isParks = /\b(park|memorial)\b/i.test(label);
      const key = isParks ? 'Parks Inspection' : 'Playground Inspection';
      const parkQuestions = inspectionData['parks']?.questionnaires[key] || [];
      return isParks ? parkQuestions : parkQuestions.slice(0, 8);
    }

    // Utility: single checklist regardless of selected site
    if (inspectionCategory === 'utility') {
      const utilQs = inspectionData['utility']?.questionnaires || {};
      const firstKey = Object.keys(utilQs)[0];
      return (firstKey && utilQs[firstKey]) ? utilQs[firstKey] : [];
    }

    // Default: category uses its own questionnaires keyed by location
    if (!selectedLocation) return [];
    return inspectionData[inspectionCategory]?.questionnaires[selectedLocation] || [];
  };

  const allQuestionsAnswered = () => {
    const questions = getCurrentQuestions();
    if (inspectionCategory === 'arena') {
      return questions.length > 0 && questions.every((_, idx) => {
        const answer = inspectionAnswers[idx];
        return answer && answer.toString().trim() !== '';
      });
    } else {
      return questions.length > 0 && questions.every((_, idx) => inspectionAnswers[idx] !== undefined);
    }
  };

  const handleCompleteInspection = () => {
    if (!allQuestionsAnswered()) {
      alert('Please answer all inspection questions');
      return;
    }
    setIsFormCompleted(true);
    setFormState('signoff');
  };

  const handleLeadSign = () => {
    setLeadSignature(true);
    alert('Lead Inspector signature recorded');
  };

  const handleParticipantSign = (participantId) => {
    setParticipantSignatures(prev => ({
      ...prev,
      [participantId]: true
    }));
    alert(`Participant signature recorded for ${employees.find(e => e.id === participantId)?.name}`);
  };

  const questions = getCurrentQuestions();
  const deficiencyCount = Object.values(inspectionAnswers).filter(isNeedsAttentionInspectionAnswer).length;
  const correctedDeficiencyCount = Object.entries(deficiencyCorrected).filter(([questionIndex, corrected]) => {
    return Boolean(corrected) && isNeedsAttentionInspectionAnswer(inspectionAnswers[questionIndex]);
  }).length;
  const sortLocationLabels = (locations) => [...locations].sort((left, right) => left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' }));
  const selectedLocationLabel = selectedLocation
    ? (
        inspectionCategory === 'parks'
          ? parseParkValue(selectedLocation).address || ''
          : inspectionCategory === 'utility'
            ? parseUtilityValue(selectedLocation).address || ''
            : selectedLocation
      )
    : '';
  // For first-aid and whmis, reuse Facility locations for context
  const availableLocations = inspectionCategory
    ? (
        inspectionCategory === 'first-aid' || inspectionCategory === 'whmis'
          ? sortLocationLabels(Object.keys(inspectionData['facility']?.questionnaires || {}))
          : inspectionCategory === 'parks'
            ? parksLocations.map(p => encodeParkValue(p.address, p.name))
            : inspectionCategory === 'utility'
              ? utilitiesLocations.map(u => encodeUtilityValue(u.address, u.name))
              : sortLocationLabels(Object.keys(inspectionData[inspectionCategory]?.questionnaires || {}))
      )
    : [];

  // Login Screen
  if (formState === 'login') {
    return (
      <div className="screen-container login-screen">
        <div className="login-card">
          <h1 className="login-title">Inspection System</h1>
          <p className="login-subtitle">Workplace Inspection Reporting</p>
          
          <div className="info-box">
            <p className="info-label">Staff Name:</p>
            <p className="info-value">{currentUserName || 'Loading...'}</p>
            
            <p className="info-label" style={{ marginTop: '1rem' }}>Date:</p>
            <p className="info-value">{currentDate}</p>
            
            <p className="info-label" style={{ marginTop: '1rem' }}>Time:</p>
            <p className="info-value">{currentTime}</p>
          </div>

          <button onClick={handleStartInspection} className="btn-primary btn-full">
            Start New Inspection
          </button>
        </div>
      </div>
    );
  }

  // Inspection Form Screen
  if (formState === 'inspection' && !isFormCompleted) {
    return (
      <div className="screen-container form-screen">
        <div className="form-container">
          <div className="form-card">
            <h1 className="form-title">Inspection Report</h1>

            <div className="header-grid">
              <div>
                <p className="header-label">Staff Name</p>
                <p className="header-value">{currentUserName || 'Loading...'}</p>
              </div>
              <div>
                <p className="header-label">Date / Time</p>
                <p className="header-value">{currentDate} {currentTime}</p>
              </div>
              <div>
                <p className="header-label">Lead Inspector</p>
                <p className="header-value">{leadInspector}</p>
              </div>
            </div>

            {/* Inspection Category */}
            <div className="form-section">
              <label className="form-label">
                Inspection Category <span className="required">*</span>
              </label>
              <select value={inspectionCategory} onChange={handleCategoryChange} className="form-select">
                <option value="">Select Category</option>
                <option value="arena">Arena/Pool Inspection</option>
                  <option value="facility">Facility Inspection</option>
                <option value="first-aid">First Aid Kit Inspection</option>
                  <option value="parks">Parks/Playground Inspection</option>
                  <option value="utility">Utility Inspection</option>
                <option value="whmis">WHMIS Evaluation</option>
              </select>
            </div>

            {/* Location Selection (Facility locations reused for First Aid and WHMIS) */}
            {inspectionCategory && availableLocations.length > 0 && (
              <div className="form-section">
                <label className="form-label">
                  {inspectionCategory === 'arena' ? 'Arena/Pool Location' : 'Location/Facility'} <span className="required">*</span>
                </label>
                <select value={selectedLocation} onChange={handleLocationChange} className="form-select">
                  <option value="">Select {inspectionCategory === 'arena' ? 'Arena/Pool' : 'Location'}</option>
                  {availableLocations.map((loc, idx) => {
                    if (inspectionCategory === 'parks') {
                      const { address, name } = parseParkValue(loc);
                      const label = `${address} — ${name}`;
                      return <option key={idx} value={loc}>{label}</option>;
                    }
                    if (inspectionCategory === 'utility') {
                      const { address, name } = parseUtilityValue(loc);
                      const label = `${address} — ${name}`;
                      return <option key={idx} value={loc}>{label}</option>;
                    }
                    return <option key={idx} value={loc}>{loc}</option>;
                  })}
                </select>
                {inspectionCategory === 'parks' && selectedLocation && (() => {
                  const { name } = parseParkValue(selectedLocation);
                  return name ? <p className="helper-text">{name}</p> : null;
                })()}
                {inspectionCategory === 'utility' && selectedLocation && (() => {
                  const { name } = parseUtilityValue(selectedLocation);
                  return name ? <p className="helper-text">{name}</p> : null;
                })()}
              </div>
            )}

            {/* First Aid Kit Type (separate dropdown) */}
            {inspectionCategory === 'first-aid' && (
              <div className="form-section">
                <label className="form-label">
                  First Aid Kit Type <span className="required">*</span>
                </label>
                <select value={selectedKitType} onChange={handleKitTypeChange} className="form-select">
                  <option value="">Select Kit Type</option>
                  {Object.keys(inspectionData['first-aid']?.questionnaires || {}).map((kit, idx) => (
                    <option key={idx} value={kit}>{kit}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Participants */}
            {selectedLocation && (
              <div className="form-section">
                <label className="form-label">
                  Inspection Participants <span className="required">*</span>
                </label>
                <div className="dropdown-container">
                  <button 
                    type="button"
                    onClick={() => setShowParticipantDropdown(!showParticipantDropdown)} 
                    className="dropdown-toggle"
                  >
                    <span>{selectedParticipants.length} participant{selectedParticipants.length !== 1 ? 's' : ''} selected</span>
                    <ChevronDown size={20} />
                  </button>
                  {showParticipantDropdown && (
                    <div className="dropdown-menu">
                      {loadingEmployees && (
                        <div className="dropdown-item" style={{ justifyContent: 'center' }}>
                          Loading employees...
                        </div>
                      )}
                      {!loadingEmployees && employeesError && (
                        <div className="dropdown-item" style={{ color: '#ef4444' }}>
                          {employeesError}
                        </div>
                      )}
                      {!loadingEmployees && !employeesError && employees.map(emp => (
                        <button
                          key={emp.id}
                          type="button"
                          onClick={() => toggleParticipant(emp.id)}
                          className={`dropdown-item ${selectedParticipants.includes(emp.id) ? 'selected' : ''}`}
                        >
                          <input 
                            type="checkbox" 
                            checked={selectedParticipants.includes(emp.id)} 
                            onChange={() => {}} 
                            className="checkbox" 
                          />
                          <div>
                            <p className="employee-name">{emp.name}</p>
                            <p className="employee-role">{emp.role}{emp.department ? ` • ${emp.department}` : ''}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Inspection Questions */}
            {(selectedLocation && (inspectionCategory !== 'first-aid' || selectedKitType)) && questions.length > 0 && (
              <div className="form-section">
                <h2 className="section-title">
                  {selectedLocation.includes('Ice Surface') ? 'Arena Ice Surface Measurements' : 
                   selectedLocation.includes('Chemical') ? 'Chemical Testing & Monitoring' : 
                   'Inspection Checklist'} - {selectedLocationLabel}
                </h2>
                {inspectionCategory === 'parks' && selectedLocation && (() => {
                  const { name } = parseParkValue(selectedLocation);
                  return name ? <p className="helper-text" style={{ marginTop: '-0.5rem' }}>{name}</p> : null;
                })()}
                {inspectionCategory === 'utility' && selectedLocation && (() => {
                  const { name } = parseUtilityValue(selectedLocation);
                  return name ? <p className="helper-text" style={{ marginTop: '-0.5rem' }}>{name}</p> : null;
                })()}
                <p className="helper-text">
                  {selectedLocation.includes('Ice Surface') 
                    ? 'Enter ice thickness measurements in decimal format (XX.XX cm). Reference the Arena Ice Surface Map for position locations.' 
                    : selectedLocation.includes('Chemical')
                    ? 'Enter water test values in decimal format (XX.X). Add text notes where applicable.'
                    : 'Check each item. If "Needs Attention", provide deficiency details below.'}
                </p>
                {selectedLocation.includes('Ice Surface') && (
                  <img
                    src={arenaMap}
                    alt="Arena Ice Surface Map"
                    className="arena-map-image"
                  />
                )}
                <div className="questions-list">
                  {questions.map((question, idx) => (
                    <div key={idx} className="question-card">
                      <p className="question-text">{idx + 1}. {question}</p>
                      {(selectedLocation.includes('Ice Surface') || selectedLocation.includes('Chemical')) ? (
                        <div>
                          <input
                            type="text"
                            value={inspectionAnswers[idx] || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (selectedLocation.includes('Ice Surface')) {
                                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                  setInspectionAnswers(prev => ({
                                    ...prev,
                                    [idx]: value
                                  }));
                                }
                              } else {
                                setInspectionAnswers(prev => ({
                                  ...prev,
                                  [idx]: value
                                }));
                              }
                            }}
                            placeholder={selectedLocation.includes('Ice Surface') ? 'Enter measurement (cm)' : 'Enter value or notes'}
                            className="measurement-input"
                          />
                        </div>
                      ) : (
                        <>
                          <div className="status-buttons">
                            <button
                              type="button"
                              onClick={() => handleAnswer(idx, 'ok')}
                              className={`status-btn ${inspectionAnswers[idx] === 'ok' ? 'status-ok active' : 'status-inactive'}`}
                            >
                              ✓ OK
                            </button>
                            <button
                              type="button"
                              onClick={() => handleAnswer(idx, 'needs-attention')}
                              className={`status-btn ${isNeedsAttentionInspectionAnswer(inspectionAnswers[idx]) ? 'status-warning active' : 'status-inactive'}`}
                            >
                              ⚠ Needs Attention
                            </button>
                          </div>
                          {isNeedsAttentionInspectionAnswer(inspectionAnswers[idx]) && (
                            <div className="deficiency-box">
                              <label className="deficiency-label">Deficiency Details</label>
                              <textarea
                                value={String(deficiencies[idx] || '')}
                                onChange={(e) => handleDeficiencyNote(idx, e.target.value)}
                                placeholder="Describe the deficiency..."
                                className="deficiency-textarea"
                                rows="3"
                              />
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={deficiencyCorrected[idx] || false}
                                  onChange={(e) => setDeficiencyCorrected(prev => ({
                                    ...prev,
                                    [idx]: e.target.checked
                                  }))}
                                  className="checkbox"
                                />
                                <span>Deficiency corrected at time of inspection</span>
                              </label>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Worker Observations - Facility Only */}
            {inspectionCategory === 'facility' && selectedLocation && (
              <div className="form-section">
                <label className="form-label">
                  Worker Observations <span className="required">*</span>
                </label>
                <p className="helper-text">Please note observations of workers, such as wearing PPE, following SWP and identify concerns that need to be forwarded to the Supervisor/Manager.</p>
                <textarea
                  value={workerObservations}
                  onChange={(e) => setWorkerObservations(e.target.value)}
                  placeholder="Enter observations..."
                  className="form-textarea"
                  rows="4"
                />
              </div>
            )}

            {/* Inspection Notes */}
            {selectedLocation && (
              <div className="form-section">
                <label className="form-label">Inspection Notes</label>
                <div className="note-input-group">
                  <input
                    type="text"
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                    placeholder="Add a note..."
                    className="note-input"
                  />
                  <button type="button" onClick={handleAddNote} className="btn-primary btn-icon">
                    <Plus size={18} /> Add
                  </button>
                </div>
                {inspectionNotes.length > 0 && (
                  <div className="notes-list">
                    {inspectionNotes.map(note => (
                      <div key={note.id} className="note-item">
                        <div className="note-header">
                          <p className="note-timestamp">{note.timestamp}</p>
                          <button 
                            type="button"
                            onClick={() => handleDeleteNote(note.id)} 
                            className="btn-icon-danger"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <p className="note-text">{note.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedLocation && (inspectionCategory !== 'first-aid' || selectedKitType) && (
              <button type="button" onClick={handleCompleteInspection} className="btn-success btn-full">
                Complete Inspection
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Sign-off Screen
  if (formState === 'signoff' && isFormCompleted) {
    const participantList = selectedParticipants
      .map(id => employees.find(e => e.id === id))
      .filter(Boolean);
    
    return (
      <div className="screen-container form-screen">
        <div className="form-container">
          <div className="form-card">
            <h1 className="form-title">Inspection Sign-off</h1>

            <div className="lead-signoff">
              <h2 className="signoff-title">Lead Inspector Acknowledgement</h2>
              <div className="acknowledgement-box">
                <p className="acknowledgement-text">"I acknowledge the inspection is completed and applicable aspects were checked and any corrective actions were documented and sent to appropriate staff."</p>
              </div>
              <p className="signoff-name"><strong>Lead Inspector:</strong> {leadInspector}</p>
              {!leadSignature ? (
                <button type="button" onClick={handleLeadSign} className="btn-primary">
                  Sign as Lead Inspector
                </button>
              ) : (
                <div className="signed-badge">
                  <Lock size={20} />
                  <span>Signed and locked</span>
                </div>
              )}
            </div>

            {participantList.length > 0 && (
              <div className="form-section">
                <h2 className="section-title">Participant Acknowledgements</h2>
                <div className="participants-list">
                  {participantList.map(participant => (
                    <div key={participant.id} className="participant-signoff">
                      <div className="acknowledgement-box">
                        <p className="acknowledgement-text participant-ack">"I acknowledge I participated in the inspection and assisted the lead inspector."</p>
                      </div>
                      <p className="signoff-name"><strong>Participant:</strong> {participant.name} ({participant.role})</p>
                      {!participantSignatures[participant.id] ? (
                        <button 
                          type="button"
                          onClick={() => handleParticipantSign(participant.id)} 
                          className="btn-secondary"
                        >
                          Sign as Participant
                        </button>
                      ) : (
                        <div className="signed-badge">
                          <Lock size={20} />
                          <span>Signed and locked</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="summary-box">
              <h3 className="summary-title">Inspection Summary</h3>
              <div className="summary-content">
                <p><strong>Category:</strong> {inspectionCategory}</p>
                <p><strong>Location:</strong> {selectedLocation}</p>
                <p><strong>Lead Inspector:</strong> {leadInspector}</p>
                <p><strong>Participants:</strong> {participantList.length > 0 ? participantList.map(p => p.name).join(', ') : 'None'}</p>
                <p><strong>Total Questions:</strong> {questions.length}</p>
                <p><strong>Deficiencies Found:</strong> {deficiencyCount}</p>
                <p><strong>Deficiencies Corrected:</strong> {correctedDeficiencyCount}</p>
                <p><strong>Notes Added:</strong> {inspectionNotes.length}</p>
              </div>
            </div>

            <button
              type="button"
              onClick={async () => {
                try {
                  // Use the actual submission time in the payload
                  const submittedAt = new Date();
                  const submittedDate = submittedAt.toISOString().split('T')[0];
                  const submittedTime = submittedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

                  const payload = {
                    category: inspectionCategory,
                    selectedLocation,
                    kitType: selectedKitType,
                    inspectorName: currentUserName,
                    leadInspector,
                    participants: selectedParticipants,
                    questionTexts: questions,
                    answers: inspectionAnswers,
                    deficiencies: buildInspectionDeficienciesPayload(inspectionAnswers, deficiencies, deficiencyCorrected),
                    notes: inspectionNotes,
                    workerObservations,
                    leadSignature,
                    participantSignatures,
                    date: submittedDate,
                    time: submittedTime,
                    submittedBy: user?.email || currentUserName
                  };
                  await api.post('/api/inspectionReports', payload);
                  alert('Inspection report submitted successfully!');
                } catch (err) {
                  console.error('Failed to submit inspection report', err);
                  alert('Failed to submit inspection report. Please try again.');
                }

                // Reset form state
                setFormState('login');
                setInspectionCategory('');
                setSelectedLocation('');
                setSelectedKitType('');
                setLeadInspector(currentUserName || '');
                setSelectedParticipants([]);
                setInspectionAnswers({});
                setDeficiencies({});
                setDeficiencyCorrected({});
                setWorkerObservations('');
                setInspectionNotes([]);
                setIsFormCompleted(false);
                setLeadSignature(false);
                setParticipantSignatures({});

                // Ensure the next inspection shows a fresh Date/Time
                refreshDateTime();
              }}
              className="btn-success btn-full submit-btn"
            >
              Submit Inspection Report
            </button>
          </div>
        </div>
      </div>
    );
  }
}